import { OpenPanel } from '@openpanel/web'

const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)

// Datacenter scrapers and headless crawlers (mostly Singapore/China ASNs) were
// inflating traffic ~2x. They execute JS, so the OpenPanel SDK was counting
// them. Skip tracking for the automation/headless signals they expose. This
// only suppresses *analytics* — crawlers still render the page for SEO.
function isLikelyBot(): boolean {
  if (typeof navigator === 'undefined') return true
  const nav = navigator as Navigator & { webdriver?: boolean }
  if (nav.webdriver) return true
  const ua = nav.userAgent || ''
  if (!ua) return true
  if (
    /HeadlessChrome|PhantomJS|Selenium|puppeteer|playwright|Electron|bot|crawler|spider|scrapy|python-requests|Go-http-client|MSIE [6-9]\./i.test(
      ua,
    )
  )
    return true
  // Headless browsers commonly report no languages.
  if (Array.isArray(nav.languages) && nav.languages.length === 0) return true
  return false
}

const clientId = import.meta.env.VITE_PUBLIC_OPENPANEL_CLIENT_ID as string | undefined
const apiUrl =
  (import.meta.env.VITE_PUBLIC_OPENPANEL_API_URL as string | undefined) ??
  'https://openpanel-api.gazar.dev'

let op: OpenPanel | null = null

// Construct the OpenPanel client. Called explicitly from main.tsx so the
// instantiation is a retained side effect (a bare side-effect import gets
// tree-shaken because nothing consumes the exports). No-ops on localhost,
// when the client ID is unset, or if called more than once.
export function initAnalytics() {
  if (op || !clientId || isLocalhost || isLikelyBot()) return op
  op = new OpenPanel({
    apiUrl,
    clientId,
    trackScreenViews: true,
    trackOutgoingLinks: true,
    trackAttributes: true,
  })
  return op
}

type Props = Record<string, unknown> | undefined

export const analytics = {
  capture(event: string, properties?: Props) {
    op?.track(event, properties)
  },
  identify(profileId: string, properties?: Props) {
    op?.identify({ profileId, ...(properties as Record<string, unknown> | undefined) })
  },
  setPersonProperties(properties: Record<string, unknown>) {
    op?.setGlobalProperties(properties)
  },
  reset() {
    op?.clear()
  },
}

export const useAnalytics = () => analytics
